import { isBefore, isEqual, isAfter, parseISO } from 'date-fns';
import type { Period } from '@/utilities/timeSeriesPeriodMap';
import { getDateRangeByPeriod } from '@/utilities/timeSeriesPeriodMap';

export interface Valuation {
  originalFairValue: number | null;
  fairValue: number | null;
  createdAt: string;
}

/**
 * Sorts valuations by date, newest first
 */
const sortByDate = (valuations: Valuation[]): Valuation[] => {
  return [...valuations].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
};

/**
 * Gets the date from a valuation's createdAt string
 */
const getValuationDate = (valuation: Valuation): Date => {
  return parseISO(valuation.createdAt);
};

const shouldIncludeValuation = (
  valuation: Valuation,
  periodStartDate: Date,
  periodEndDate: Date
): boolean => {
  const valuationDate = getValuationDate(valuation);

  return (
    !isBefore(valuationDate, periodStartDate) &&
    !isAfter(valuationDate, periodEndDate)
  );
};

/**
 * Filters valuations based on the period and date.
 * If there are no valuations within the period, it will return the most recent valuation.
 */
export const filterValuations = (
  valuations: Valuation[] | null | undefined,
  period: Period,
  date?: Date
): Valuation[] => {
  if (!valuations?.length) return [];

  const { startDate: startDateString, endDate: endDateString } =
    getDateRangeByPeriod(period, date);
  const startDate = parseISO(startDateString);
  const endDate = parseISO(endDateString);
  const sortedValuations = sortByDate(valuations);

  // Find valuations within the period
  const withinPeriod = sortedValuations.filter((v) => {
    const isWithinPeriod = shouldIncludeValuation(v, startDate, endDate);
    const isAtPeriodStart = isEqual(getValuationDate(v), startDate);
    const isAtPeriodEnd = isEqual(getValuationDate(v), endDate);
    return isWithinPeriod || isAtPeriodStart || isAtPeriodEnd;
  });

  if (withinPeriod.length > 0) {
    return withinPeriod;
  }

  return [sortedValuations[0]];
};
